import React, { useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { FileText, FileSpreadsheet } from 'lucide-react';
import { buildWorksheetFromRows, exportWorkbookToXlsx, type ExcelColumn } from '../../utils/excelExport';
import './GelatoDataGrid.css';

// Registra tutti i moduli Community di AG Grid
ModuleRegistry.registerModules([AllCommunityModule]);

// Tema unico delle griglie, allineato ai token del design system.
// Da usare anche nelle pagine che istanziano AgGridReact direttamente.
export const gelatoGridTheme = themeQuartz.withParams({
  accentColor: '#7d8202',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  headerBackgroundColor: '#fafaf9',
  headerTextColor: '#44403c',
  borderColor: '#e7e5e4',
  rowHoverColor: '#f8f8ec',
  wrapperBorderRadius: 8,
});

interface GelatoDataGridProps {
  rowData: any[];
  columnDefs: ColDef[];
  loading?: boolean;
  pagination?: boolean;
  paginationPageSize?: number;
  pinnedBottomRowData?: any[];
  onGridReady?: (event: GridReadyEvent) => void;
  onRowClicked?: (event: any) => void;
  onRowDoubleClicked?: (event: any) => void;
  onCellClicked?: (event: any) => void;
  height?: string;
  className?: string;
  defaultColDef?: ColDef;
  exportable?: boolean;
  onExportExcel?: () => void;
  onExportCsv?: () => void;
  excelFileName?: string;
  csvFileName?: string;
  suppressCellFocus?: boolean;
  context?: any;
  getRowStyle?: (params: any) => any;
}

const GelatoDataGrid: React.FC<GelatoDataGridProps> = ({
  rowData,
  columnDefs,
  loading = false,
  pagination = true,
  paginationPageSize = 50,
  pinnedBottomRowData,
  onGridReady,
  onRowClicked,
  onRowDoubleClicked,
  onCellClicked,
  height = '600px',
  className = '',
  defaultColDef,
  exportable = false,
  onExportExcel,
  onExportCsv,
  excelFileName,
  csvFileName,
  suppressCellFocus,
  context,
  getRowStyle
}) => {
  // Configurazione di default per le colonne (solo Community features)
  const defaultColumnDef = useMemo<ColDef>(() => ({
    sortable: true,
    filter: true, // Filtro di base
    resizable: true,
    flex: 1,
    minWidth: 100,
    ...defaultColDef
  }), [defaultColDef]);

  // Configurazione griglia (solo Community features)
  const gridOptions = useMemo(() => ({
    animateRows: true,
    suppressColumnVirtualisation: false,
    suppressRowVirtualisation: false,
    pagination,
    paginationPageSize,
    paginationPageSizeSelector: [20, 50, 100, 200, 500],
    localeText: {
      // Localizzazione italiana
      page: 'Pagina',
      more: 'Più',
      to: 'a',
      of: 'di',
      next: 'Successiva',
      previous: 'Precedente',
      loadingOoo: 'Caricamento...',
      selectAll: 'Seleziona tutto',
      searchOoo: 'Cerca...',
      blanks: 'Vuoti',
      filterOoo: 'Filtra...',
      equals: 'Uguale',
      notEquals: 'Diverso',
      lessThan: 'Minore di',
      greaterThan: 'Maggiore di',
      contains: 'Contiene',
      notContains: 'Non contiene',
      startsWith: 'Inizia con',
      endsWith: 'Finisce con',
      andCondition: 'E',
      orCondition: 'O',
      applyFilter: 'Applica',
      resetFilter: 'Reset',
      clearFilter: 'Pulisci',
      noRowsToShow: 'Nessun dato da mostrare'
    }
  }), [pagination, paginationPageSize]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    if (onGridReady) {
      onGridReady(event);
    }
  }, [onGridReady]);

  const getColumnExportValue = useCallback((colDef: ColDef, row: any) => {
    const field = (colDef as any).field as string | undefined;
    let rawValue: any = field ? row?.[field] : undefined;

    // Supporta valueGetter funzione (non string) quando presente
    const valueGetter = (colDef as any).valueGetter;
    if (typeof valueGetter === 'function') {
      try {
        rawValue = valueGetter({ data: row, colDef, value: rawValue });
      } catch {
        // noop
      }
    }

    const valueFormatter = (colDef as any).valueFormatter;
    if (typeof valueFormatter === 'function') {
      try {
        return valueFormatter({ value: rawValue, data: row, colDef });
      } catch {
        return rawValue;
      }
    }

    return rawValue;
  }, []);

  const exportToCsvFallback = useCallback(() => {
    const cols = (columnDefs || []).filter(c => !!(c as any).field || typeof (c as any).valueGetter === 'function');
    const headers = cols.map(c => (c.headerName || (c as any).field || '') as string);
    const lines = [
      headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','),
      ...rowData.map(r =>
        cols
          .map(c => {
            const v = getColumnExportValue(c, r);
            const s = v === null || v === undefined ? '' : String(v);
            return `"${s.replace(/"/g, '""')}"`;
          })
          .join(',')
      )
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (csvFileName && csvFileName.endsWith('.csv')) ? csvFileName : (csvFileName ? `${csvFileName}.csv` : 'export.csv');
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [columnDefs, csvFileName, getColumnExportValue, rowData]);

  const exportToExcelFallback = useCallback(() => {
    const cols = (columnDefs || []).filter(c => !!(c as any).field || typeof (c as any).valueGetter === 'function');
    const excelColumns: ExcelColumn<any>[] = cols.map(c => ({
      header: (c.headerName || (c as any).field || '') as string,
      value: (row) => getColumnExportValue(c, row)
    }));

    const worksheet = buildWorksheetFromRows(rowData, excelColumns);
    exportWorkbookToXlsx(excelFileName || 'export.xlsx', [
      { name: 'Dati', worksheet }
    ]);
  }, [columnDefs, excelFileName, getColumnExportValue, rowData]);

  const exportToCsv = useCallback(() => {
    if (onExportCsv) {
      onExportCsv();
      return;
    }
    exportToCsvFallback();
  }, [exportToCsvFallback, onExportCsv]);

  const exportToExcel = useCallback(() => {
    if (onExportExcel) {
      onExportExcel();
      return;
    }
    exportToExcelFallback();
  }, [exportToExcelFallback, onExportExcel]);

  return (
    <div className={`gelato-data-grid-container ${className}`}>
      {exportable && (
        <div className="grid-toolbar">
          <div className="grid-info">
            <span>{rowData.length} record trovati</span>
          </div>
          <div className="grid-actions">
            <button
              className="btn-export btn-export-csv"
              onClick={exportToCsv}
              title="Esporta CSV"
            >
              <FileText size={16} /> CSV
            </button>
            <button
              className="btn-export btn-export-excel"
              onClick={exportToExcel}
              title="Esporta Excel"
            >
              <FileSpreadsheet size={16} /> Excel
            </button>
          </div>
        </div>
      )}
      
      <div
        className={`gelato-grid ${loading ? 'loading' : ''}`}
        style={{ height }}
      >
        {loading && (
          <div className="grid-loading-overlay">
            <div className="loading-spinner"></div>
            <span>Caricamento dati...</span>
          </div>
        )}
        
        <AgGridReact
          theme={gelatoGridTheme}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColumnDef}
          gridOptions={gridOptions}
          onGridReady={handleGridReady}
          onRowClicked={onRowClicked}
          onRowDoubleClicked={onRowDoubleClicked}
          onCellClicked={onCellClicked}
          suppressMenuHide={true}
          pinnedBottomRowData={pinnedBottomRowData}
          suppressCellFocus={suppressCellFocus}
          context={context}
          getRowStyle={getRowStyle}
        />
      </div>
    </div>
  );
};

export default GelatoDataGrid;
